import {host_getsByHost_api} from 'apis'
import {getId, getType} from 'apis/model/base'
import classNames from 'classnames'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import {NavigationContext} from 'modules/navigation/NavigationContext'
import React, {useCallback, useContext, useMemo} from 'react'
import {IoCaretDown, IoCaretForward} from 'react-icons/io5'
import {useLocation} from 'react-router-dom'
import {useToggle} from 'react-use'
import {branch, compose, fromRenderProps, nest, pure, renderNothing,} from 'recompose'
import {ExpandablePanel} from 'views/AppLocals/custom/AdvancedFormGroup'
import EntityList from 'views/AppLocals/EntityList'
import {HostsSortBy} from 'views/AppLocals/factory/createEntitySortBy'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper from 'views/AppLocals/hooks/usePaginationHelper'
import {renderHostOwner} from 'views/AppLocals/Host/functions/renderHost'
import HostContext, {HostProvider} from 'views/AppLocals/Host/HostContext'
import ListHeader from 'views/AppLocals/Templates/items/ListHeader'
import {NullAsDefault, renderIf} from 'views/Shared'
import WorkspaceContext from '../WorkspaceContext';
import HostsOwnerAdvancedFilter from './HostsOwnerAdvancedFilter'
import {useRouteSearchParam} from "../../../envs/_current/history";
import {Button} from "antd";
import {BsCaretRightFill} from "react-icons/bs";

const ExpandContentWrapper = ({children}) => (
    <div className="flex flex-col divider-y divider-gray-200 list-item-workspace">
        {children}
    </div>
)

const ExpandWrapper = ({children}) => children && (
    <div className="border border-color-50 hover:border-secondary-500 background hover:shadow-md rounded-xl">
        {children}
    </div>
)

const renderExpandHeader =
    ({count} = {}) =>
        ({title, onClick, expanded}) => {
            return renderIf(
                count,
                <Translate>
                    {(t) => (
                        <div
                            className={classNames(
                                'px-3 py-2 flex items-center gap-2 transition-all duration-300 cursor-pointer',
                                !!expanded ?
                                    'text-secondary-600 bg-secondary-50 rounded-t-xl' :
                                    'text-gray-600 hover:text-secondary hover:bg-secondary-50 rounded-xl',
                            )}
                            onClick={onClick}>
                            <BsCaretRightFill size={14} style={{ transform: !!expanded ? 'rotate(90deg)' : '', transition: 'transform 0.3s' }}/>
                            <span className="text-sm font-medium">{t(title)}</span>
                            <span className="text-sm font-semibold">{`(${count})`}</span>
                        </div>
                    )}
                </Translate>
            )
        }

const renderCollapsedContent = (child) => (
    <div
        style={{
            visibility: 'hidden',
            maxHeight: 0,
        }}>
        {child}
    </div>
)

const SubordinateUnits = () => {
    const location = useLocation()

    const [isToggle, toggle] = useToggle()

    const {container} = useContext(HostContext)

    const [id, prop] = [getId(container), getType(container)]

    const refreshToken = _.get(location.state, 'refreshToken')

    const renderWrap = useCallback((children, __, count) => count > 0 && (
        <ExpandablePanel
            onClick={toggle}
            expanded={isToggle}
            title="pending list"
            ContentWrapper={ExpandContentWrapper}
            Header={renderExpandHeader({count})}
            Wrapper={count > 0 ? ExpandWrapper : <div />}
            renderCollapsedContent={renderCollapsedContent}>
            {children}
        </ExpandablePanel>
    ), [isToggle])

    return (
        <EntityList
            key={refreshToken}
            renderWrap={renderWrap}
            values={{
                request_status: -1,
            }}
            apiInfo={host_getsByHost_api}
            RenderEntity={React.Fragment}
            query={bindQueryParams([{id}, {prop}])}
            renderItem={(item) => {
                return (
                    renderHostOwner({
                        request_status: -1,
                    })
                )
            }}
        />
    )
}

export const SubordinateUnitList = ({
                                        Header = ListHeader,
                                        Wrapper = 'div',
                                        filter_actions = {
                                            sortBy: 'sort-by',
                                            keyword: 'keyword',
                                            status: 'status',
                                        },
                                    }) => {
    const t = useTranslate()

    const {container} = useContext(HostContext)

    const location = useLocation()

    const {current} = useContext(NavigationContext)

    const refreshToken = useMemo(
        () =>
            JSON.stringify({
                token: _.get(location.state, 'refreshToken'),
                dependencies: [current],
            }),
        [current, location.state]
    )

    const [id, prop] = [getId(container), getType(container)]

    const status = useRouteSearchParam(filter_actions.status)

    const sortBy = useRouteSearchParam(filter_actions.sortBy)

    const [pager_widget] = usePaginationHelper({
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({
                status,
            })
            onSortChange(sortBy)
        },
        pagerInfo: {
            apiInfo: host_getsByHost_api,
            query: bindQueryParams([{id}, {prop,},]),
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            renderItem: renderHostOwner({
                request_status: 1,
            }),
            renderWrap: (child) => (
                <div className="mt-2 flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            ),
        },
        withValues: (values) => ({
            ...values,
            owner_type: prop,
            request_status: 1,
        }),
        withKey: ({sortBy, keyword, filterBy}) =>
            [id, sortBy, keyword, JSON.stringify(filterBy), refreshToken].join('/'),
        filter_params: {
            owner_id: id,
            owner_type: prop,
        },
        query_params: filter_actions,
        dependencies: [id, prop],
        sortHeader: t('subordinate units'),
        SortByWidget: HostsSortBy,
        AdvancedFilter: HostsOwnerAdvancedFilter,
        renderHeader: ([searchBar, filterBar]) => (
            <div className="flex flex-col gap-4">
                <div className="mb-2 flex items-center gap-3">
                    <div className="flex flex-col flex-1">
                        <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                            {t('subordinate units')}
                        </h2>
                        <div className="mt-1 w-20 h-1 rounded-full bg-gray-400"/>
                    </div>
                </div>
                {filterBar}
                {searchBar}
            </div>
        ),
    })

    return (
        <Wrapper className="flex flex-col space-y-3">
            {/*<Header title="subordinate units"/>*/}
            <SubordinateUnits/>
            {pager_widget}
            <div className="h-16"/>
        </Wrapper>
    )
}

export default compose(
    fromRenderProps(WorkspaceContext.Consumer, ({organization}) => ({
        container: organization,
    })),
    branch(({container}) => !!!getId(container), renderNothing),
    pure
)(
    nest(
        ({container, children}) => (
            <HostProvider
                container={container}
                autoRefreshKey="refreshToken">
                {children}
            </HostProvider>
        ),
        SubordinateUnitList
    )
)
